import type { LucideIcon } from 'lucide-react';

import { Typography, type TypographyProps } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon;
  title: string;
  titleAs?: TypographyProps['as'];
  description?: React.ReactNode;
  action?: React.ReactNode;
}

/**
 * Hero empty-state pattern: icon, headline, supporting copy and optional call-to-action.
 *
 * @category Data
 */
function EmptyState({
  icon: Icon,
  title,
  titleAs = 'h2',
  description,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center px-5 py-12',
        className,
      )}
      {...props}
    >
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        {Icon && (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon className="h-8 w-8" aria-hidden="true" />
          </div>
        )}
        <Typography as={titleAs} variant="heading-md">
          {title}
        </Typography>
        {description && (
          <Typography as="p" variant="body" className="text-muted-foreground">
            {description}
          </Typography>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

export { EmptyState };
export type { EmptyStateProps };
